package com.schoolconnect.app.feature.staff

import com.schoolconnect.app.datasource.network.UserSessionStore
import com.schoolconnect.app.datasource.network.NetworkActivityTracker
import com.schoolconnect.app.datasource.network.request.SchoolStaffCreateRequest
import com.schoolconnect.app.datasource.network.request.AddStaffWithAssignmentsRequest
import com.schoolconnect.app.datasource.network.request.TeachingAssignmentInput
import com.schoolconnect.app.datasource.network.request.SchoolStaffUpdateRequest
import com.schoolconnect.app.datasource.network.request.UpdateStaffWithAssignmentsRequest
import com.schoolconnect.app.datasource.network.request.SchoolStaffFilterRequest
import com.schoolconnect.app.designsystem.appDateInputToApi
import com.schoolconnect.app.designsystem.AppToastCenter
import com.schoolconnect.app.datasource.network.response.SchoolStaffResponse
import com.schoolconnect.app.datasource.network.response.SchoolStaffWithAssignmentsResponse
import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import com.schoolconnect.app.feature.common.DebouncedActionRunner
import com.schoolconnect.app.feature.common.AdminBootstrapSequencer
import com.schoolconnect.app.feature.common.AdminPagedListCoordinator
import com.schoolconnect.app.feature.common.compareBySmartText
import com.schoolconnect.app.feature.common.offset
import com.schoolconnect.app.feature.common.resolveServerPageLoadState
import com.schoolconnect.app.feature.common.withPageIndex
import com.schoolconnect.app.feature.common.withPageSize
import com.schoolconnect.app.feature.common.ServerSort
import com.schoolconnect.app.feature.common.ServerSortOrder
import com.schoolconnect.app.repository.schoolstaff.SchoolStaffRepository
import com.schoolconnect.app.repository.classsection.ClassSectionRepository
import com.schoolconnect.app.repository.classsubjectlink.ClassSubjectLinkRepository
import com.schoolconnect.app.repository.subject.SubjectRepository
import com.schoolconnect.app.repository.role.RoleLookupRepository
import com.schoolconnect.app.repository.systemlookup.SystemLookupRepository
import com.schoolconnect.app.domain.SchoolKeys
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Semaphore
import kotlinx.coroutines.sync.withPermit

private data class SchoolStaffSearchFields(
    val name: String? = null,
    val employeeId: String? = null,
    val mobile: String? = null
)

class StaffViewModel(
    schoolId: String = ""
) {
    private companion object {
        const val TAG = "[DATA_RETRIEVE_SC] StaffViewModel"
    }

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val staffReloadDebouncer = DebouncedActionRunner(scope)
    private val bootstrapSequencer = AdminBootstrapSequencer(scope, TAG)
    private val pagedListCoordinator = AdminPagedListCoordinator(staffReloadDebouncer, bootstrapSequencer)
    private val normalizedSchoolId = schoolId.trim().ifBlank { UserSessionStore.getHomeRoleSchoolId().trim() }
    private val _uiState = MutableStateFlow(
        StaffUiState(
            isLoading = normalizedSchoolId.isNotBlank(),
            authRoleOptions = defaultStaffAuthRoleOptions()
        )
    )

    val uiState: StateFlow<StaffUiState> = _uiState.asStateFlow()

    init {
        if (normalizedSchoolId.isBlank()) {
            _uiState.update {
                it.copy(
                    isLoading = false,
                    errorMessage = "School context was not available for staff loading."
                )
            }
        } else {
            loadAuthRoles(forceRemote = false)
            loadAssignmentOptions(forceRemote = false)
            scope.launch {
                // Staff rows carry the backend gender code. Load its lookup before
                // the first page so list and detail views do not default unknown
                // codes to Male while the lookup request is still in flight.
                SystemLookupRepository.loadCategoryItems("GENDER")
                pagedListCoordinator.refreshPage(
                    forceRemote = false,
                    loadPage = { forceRemote -> refreshStaffPage(forceRemote = forceRemote) }
                )
            }
        }
    }

    fun onEvent(event: StaffUiEvent) {
        when (event) {
            StaffUiEvent.BackClicked -> Unit
            is StaffUiEvent.SearchChanged -> updateFilters(
                searchQuery = event.query,
                resetPage = true
            )
            StaffUiEvent.FilterModalOpened -> _uiState.update {
                it.copy(isFilterModalOpen = true, errorMessage = "", infoMessage = "")
            }
            StaffUiEvent.FilterModalClosed -> _uiState.update {
                it.copy(isFilterModalOpen = false)
            }
            is StaffUiEvent.FiltersApplied -> _uiState.update {
                it.copy(
                    searchQuery = event.values.searchQuery,
                    filters = event.values,
                    isFilterModalOpen = false,
                    paging = it.paging.withPageIndex(0),
                    errorMessage = "",
                    infoMessage = ""
                )
            }.also {
                requestStaffPageReload(forceRemote = false)
            }
            StaffUiEvent.FiltersReset -> _uiState.update {
                it.copy(
                    filters = StaffFilterFormValues(),
                    searchQuery = "",
                    paging = it.paging.withPageIndex(0),
                    errorMessage = "",
                    infoMessage = "Filters cleared"
                )
            }.also {
                requestStaffPageReload(forceRemote = false)
            }
            is StaffUiEvent.SortChanged -> {
                _uiState.update {
                    val nextAscending = if (it.sortField == event.field) !it.sortAscending else true
                    it.copy(
                        sortField = event.field,
                        sortAscending = nextAscending,
                        paging = it.paging.withPageIndex(0)
                    )
                }
                requestStaffPageReload(forceRemote = true)
            }
            is StaffUiEvent.PageChanged -> {
                _uiState.update { it.copy(paging = it.paging.withPageIndex(event.pageIndex)) }
                requestStaffPageReload(forceRemote = false)
            }
            is StaffUiEvent.PageSizeChanged -> {
                _uiState.update { it.copy(paging = it.paging.withPageSize(event.pageSize)) }
                requestStaffPageReload(forceRemote = false)
            }
            StaffUiEvent.RefreshRequested -> {
                queueStaffRefresh(forceRemote = true)
            }
            StaffUiEvent.AddStaffClicked -> _uiState.update {
                it.copy(
                    isAddStaffModalOpen = true,
                    isEditStaffModalOpen = false,
                    editingStaffId = "",
                    errorMessage = "",
                    infoMessage = ""
                )
            }.also { loadAssignmentOptions(forceRemote = true) }
            StaffUiEvent.AddStaffModalClosed -> _uiState.update {
                it.copy(isAddStaffModalOpen = false, isSaving = false, errorMessage = "", infoMessage = "")
            }
            StaffUiEvent.AssignmentOptionsRetryRequested -> loadAssignmentOptions(forceRemote = true)
            is StaffUiEvent.AddStaffSubmitted -> {
                if (!_uiState.value.isSaving) saveStaff(staffId = null, values = event.values)
            }
            is StaffUiEvent.EditStaff -> openEditStaff(event.staffId)
            StaffUiEvent.EditStaffModalClosed -> _uiState.update {
                it.copy(isEditStaffModalOpen = false, editingStaffId = "", editingStaffFormValues = null, isEditingStaffLoading = false, isSaving = false, errorMessage = "", infoMessage = "")
            }
            is StaffUiEvent.EditStaffSubmitted -> {
                if (!_uiState.value.isSaving) saveStaff(staffId = event.staffId, values = event.values)
            }
            is StaffUiEvent.ViewStaff -> openViewStaff(event.staffId)
            StaffUiEvent.ViewStaffModalClosed -> _uiState.update {
                it.copy(isViewStaffModalOpen = false, viewingStaffId = "")
            }
            is StaffUiEvent.ToggleStatus -> toggleStaffStatus(event.staffId)
            is StaffUiEvent.DeleteStaff -> deleteStaff(event.staffId)
            StaffUiEvent.ClearMessages -> _uiState.update {
                it.copy(errorMessage = "", infoMessage = "")
            }
        }
    }

    fun clear() {
        pagedListCoordinator.clear()
        scope.cancel()
    }

    private fun loadAuthRoles(forceRemote: Boolean) {
        _uiState.update { it.copy(isAuthRolesLoading = true) }
        scope.launch {
            RoleLookupRepository.refreshSchoolAdminRoles(forceRemote).fold(
                onSuccess = { roles ->
                    _uiState.update {
                        it.copy(
                            authRoleOptions = staffAuthRoleOptionsFromApi(roles),
                            isAuthRolesLoading = false
                        )
                    }
                },
                onFailure = { throwable ->
                    _uiState.update {
                        it.copy(
                            isAuthRolesLoading = false,
                            errorMessage = throwable.message.orEmpty().ifBlank { "Unable to load auth roles." }
                        )
                    }
                }
            )
        }
    }

    private fun loadAssignmentOptions(forceRemote: Boolean) {
        if (_uiState.value.isAssignmentOptionsLoading) return
        _uiState.update { it.copy(isAssignmentOptionsLoading = true) }
        scope.launch {
            val classSections = ClassSectionRepository.loadSchoolClassSectionLookup(
                normalizedSchoolId,
                forceRemote = forceRemote
            )
            val subjects = SubjectRepository.loadSchoolSubjectsPage(
                schoolId = normalizedSchoolId,
                isActive = true,
                pageIndex = 0,
                pageSize = 100,
                forceRemote = forceRemote
            )
            val subjectRows = subjects.getOrNull().orEmpty()
            val subjectOptions = subjectRows.mapNotNull { row ->
                row.id?.trim()?.takeIf(String::isNotBlank)?.let { id ->
                    StaffSubjectOption(id, row.name.orEmpty().ifBlank { row.shortCode.orEmpty() })
                }
            }
            val classRows = classSections.getOrNull().orEmpty()
            val linkResults = coroutineScope {
                val requestLimit = Semaphore(permits = 6)
                classRows.mapNotNull { row ->
                    row.id?.trim()?.takeIf(String::isNotBlank)
                }.distinct().map { classSectionId ->
                    async {
                        classSectionId to requestLimit.withPermit {
                            ClassSubjectLinkRepository.loadClassSectionSubjectLinks(
                                schoolId = normalizedSchoolId,
                                classSectionId = classSectionId,
                                forceRemote = forceRemote
                            )
                        }
                    }
                }.awaitAll()
            }
            val linkFailure = linkResults.firstOrNull { (_, result) -> result.isFailure }
                ?.second
                ?.exceptionOrNull()
            val activeSubjectIdsByClass = linkResults.mapNotNull { (classSectionId, result) ->
                result.getOrNull()?.mapNotNull { it.subjectId?.trim()?.takeIf(String::isNotBlank) }
                    ?.toSet()
                    ?.let { classSectionId to it }
            }.toMap()
            val activeSubjectIds = subjectRows.mapNotNull { row ->
                row.id?.trim()?.takeIf(String::isNotBlank)
            }.toSet()
            val optionsByClassSectionId = classRows.mapNotNull { row ->
                val classSectionId = row.id?.trim()?.takeIf(String::isNotBlank) ?: return@mapNotNull null
                val activeClassSubjectIds = activeSubjectIdsByClass[classSectionId].orEmpty()
                classSectionId to subjectOptions.filter { option ->
                    option.id in activeClassSubjectIds && option.id in activeSubjectIds
                }
            }.toMap()
            val classOptions = classRows.mapNotNull { row ->
                row.id?.trim()?.takeIf(String::isNotBlank)?.let { id ->
                    StaffClassSectionOption(
                        id = id,
                        label = listOf(row.className.orEmpty(), row.sectionName.orEmpty())
                            .filter(String::isNotBlank)
                            .joinToString(" - ")
                    )
                }
            }.sortedWith(compareBySmartText { it.label })
            val lookupFailure = classSections.exceptionOrNull() ?: subjects.exceptionOrNull() ?: linkFailure
            _uiState.update {
                it.copy(
                    classSectionOptions = classOptions,
                    subjectOptions = subjectOptions,
                    subjectOptionsByClassSectionId = optionsByClassSectionId,
                    isAssignmentOptionsLoading = false,
                    assignmentOptionsError = lookupFailure?.message.orEmpty().ifBlank {
                        if (classOptions.isEmpty() || subjectOptions.isEmpty()) {
                            "Class and subject options could not be loaded. Please retry."
                        } else ""
                    }
                )
            }
        }
    }

    private fun ensureAssignmentOptions() {
        val state = _uiState.value
        if (!state.isAssignmentOptionsLoading &&
            (state.classSectionOptions.isEmpty() || state.subjectOptions.isEmpty() || state.assignmentOptionsError.isNotBlank())
        ) {
            loadAssignmentOptions(forceRemote = true)
        }
    }

    private fun updateFilters(
        searchQuery: String = _uiState.value.searchQuery,
        resetPage: Boolean
    ) {
        _uiState.update {
            it.copy(
                searchQuery = searchQuery,
                filters = it.filters.copy(searchQuery = searchQuery),
                paging = if (resetPage) it.paging.withPageIndex(0) else it.paging,
                errorMessage = "",
                infoMessage = ""
            )
        }
        requestStaffPageReload(forceRemote = false)
    }

    private fun requestStaffPageReload(
        forceRemote: Boolean,
        immediate: Boolean = false
    ) {
        pagedListCoordinator.requestPage(forceRemote = forceRemote, immediate = immediate) { pageForceRemote ->
            refreshStaffPage(forceRemote = pageForceRemote)
        }
    }

    private fun queueStaffRefresh(forceRemote: Boolean) {
        _uiState.update {
            it.copy(
                isLoading = true,
                errorMessage = "",
                infoMessage = ""
            )
        }
        pagedListCoordinator.refreshPage(
            forceRemote = forceRemote,
            loadPage = { pageForceRemote -> refreshStaffPage(forceRemote = pageForceRemote) }
        )
    }

    private fun refreshStaffAfterMutation() {
        pagedListCoordinator.refreshPage(
            forceRemote = true,
            loadPage = { pageForceRemote -> refreshStaffPage(forceRemote = pageForceRemote) }
        )
    }

    private suspend fun refreshStaffPage(forceRemote: Boolean) =
        NetworkActivityTracker.withoutBlockingLoader {
            refreshStaffPageInternal(forceRemote)
        }

    private suspend fun refreshStaffPageInternal(forceRemote: Boolean) {
        val currentState = _uiState.value
        val paging = currentState.paging
        val query = currentState.searchQuery.trim()
        val roleTitle = currentState.filters.staffRole.trim().takeIf { it.isNotBlank() }
        val isActive = currentState.filters.status.toActiveFilter()
        val hasFilters = query.isNotBlank() ||
            currentState.filters.staffRole.isNotBlank() ||
            currentState.filters.authRole.isNotBlank() ||
            currentState.filters.gender.isNotBlank() ||
            currentState.filters.status.isNotBlank() ||
            currentState.filters.createdFrom.isNotBlank() ||
            currentState.filters.createdTo.isNotBlank()
        val searchFields = when {
            query.matches(Regex("^EMP\\d+$", RegexOption.IGNORE_CASE)) -> SchoolStaffSearchFields(employeeId = query)
            // Numeric fragments are valid employee IDs too (for example,
            // "08" should match an ID such as "Ger08"). The regular GET
            // search also supports mobile matching when no advanced filters
            // are active, so do not route every numeric query to `mobile`.
            query.matches(Regex("^\\d+$")) -> SchoolStaffSearchFields(employeeId = query)
            else -> SchoolStaffSearchFields(name = query.ifBlank { null })
        }
        // Staff role, name, and employee ID are supported by the normal
        // paginated GET endpoint (`role_title` and `q`). Use the advanced
        // POST endpoint only when a filter cannot be represented by GET.
        val requiresAdvancedFilter =
            currentState.filters.authRole.isNotBlank() ||
                currentState.filters.gender.isNotBlank() ||
                currentState.filters.status.isNotBlank() ||
                currentState.filters.createdFrom.isNotBlank() ||
                currentState.filters.createdTo.isNotBlank() ||
                searchFields.mobile != null
        val advancedFilter = if (requiresAdvancedFilter) {
            SchoolStaffFilterRequest(
                staffRole = currentState.filters.staffRole.trim().ifBlank { null },
                authRole = staffAuthRoleCode(
                    roleLabel = currentState.filters.authRole,
                    options = currentState.authRoleOptions
                ).trim().ifBlank { null },
                gender = currentState.filters.gender.trim().ifBlank { null }?.let {
                    com.schoolconnect.app.repository.systemlookup.SystemLookupOptions.serverCode("GENDER", it)
                },
                status = currentState.filters.status.trim().ifBlank { null },
                createdDateFrom = appDateInputToApi(currentState.filters.createdFrom).orEmpty().ifBlank { null },
                createdDateTo = appDateInputToApi(currentState.filters.createdTo).orEmpty().ifBlank { null },
                name = searchFields.name,
                employeeId = searchFields.employeeId,
                mobile = searchFields.mobile
            )
        } else null

        scLog(
            "$TAG refreshStaffPage start q=${query.ifBlank { "<blank>" }} role=${roleTitle ?: "<any>"} status=${currentState.filters.status.ifBlank { "<any>" }} pageIndex=${paging.pageIndex} pageSize=${paging.pageSize} offset=${paging.offset()} forceRemote=$forceRemote"
        )
        _uiState.update {
            it.copy(
                isLoading = true,
                errorMessage = "",
                infoMessage = ""
            )
        }

        val result = SchoolStaffRepository.loadSchoolStaffPage(
            schoolId = normalizedSchoolId,
            query = query,
            roleTitle = roleTitle,
            isActive = isActive,
            advancedFilter = advancedFilter,
            pageIndex = paging.pageIndex,
            pageSize = paging.pageSize,
            sort = ServerSort(
                field = currentState.sortField.apiField,
                order = if (currentState.sortAscending) ServerSortOrder.ASC else ServerSortOrder.DESC
            ),
            forceRemote = forceRemote
        )

        result.fold(
            onSuccess = { rows ->
                val serverTotalCount = rows.serverTotalCountOrNull()
                val mappedRows = rows.map(::toStaffRowUi)
                val pageLoad = resolveServerPageLoadState(
                    exactTotal = serverTotalCount,
                    paging = paging,
                    currentItemCount = mappedRows.size,
                    pageOffset = paging.offset()
                )
                if (pageLoad.clampedPageIndex != pageLoad.paging.pageIndex) {
                    _uiState.update { current ->
                        current.copy(
                            paging = current.paging.withPageIndex(pageLoad.clampedPageIndex),
                            isLoading = false
                        )
                    }
                    refreshStaffPage(forceRemote = forceRemote)
                    return@fold
                }

                _uiState.update { current ->
                    current.copy(
                        isLoading = false,
                        staff = mappedRows,
                        paging = pageLoad.paging,
                        errorMessage = "",
                        infoMessage = current.infoMessage
                    )
                }
                scLog(
                    "$TAG refreshStaffPage success school=$normalizedSchoolId count=${mappedRows.size} totalCount=${pageLoad.paging.totalCount} exact=${pageLoad.paging.isTotalCountExact}"
                )
            },
            onFailure = { throwable ->
                scLog("$TAG refreshStaffPage failure school=$normalizedSchoolId", throwable)
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        errorMessage = throwable.message.orEmpty().ifBlank { "Unable to load staff records." }
                    )
                }
            }
        )
    }

    private fun toggleStaffStatus(staffId: String) {
        val row = _uiState.value.staff.firstOrNull { it.id == staffId }
        if (row == null) {
            _uiState.update {
                it.copy(errorMessage = "Staff not found.", infoMessage = "")
            }
            return
        }

        val currentUserId = UserSessionStore.getUserId().trim().takeIf { it.isNotBlank() }
        val nextActive = row.status != StaffStatusUi.ACTIVE
        val actionLabel = if (nextActive) "Activated" else "Inactivated"

        _uiState.update {
            it.copy(
                isLoading = true,
                errorMessage = "",
                infoMessage = ""
            )
        }

        scope.launch {
            try {
                SchoolStaffRepository.toggleSchoolStaffStatus(
                    staffId = row.id,
                    isActive = nextActive,
                    currentUserId = currentUserId
                ).getOrThrow()

                refreshStaffAfterMutation()
                _uiState.update {
                    it.copy(
                        isLoading = true,
                        infoMessage = "$actionLabel ${row.staffName}"
                    )
                }
                scLog(
                    "$TAG toggleStaffStatus success school=$normalizedSchoolId staffId=${row.id} active=$nextActive"
                )
            } catch (throwable: Throwable) {
                scLog("$TAG toggleStaffStatus failure school=$normalizedSchoolId staffId=${row.id}", throwable)
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        errorMessage = throwable.message.orEmpty().ifBlank { "Failed to update staff status." }
                    )
                }
            }
        }
    }

    private fun deleteStaff(staffId: String) {
        val row = _uiState.value.staff.firstOrNull { it.id == staffId }
        if (row == null) {
            _uiState.update {
                it.copy(errorMessage = "Staff not found.", infoMessage = "")
            }
            return
        }

        _uiState.update {
            it.copy(
                isLoading = true,
                errorMessage = "",
                infoMessage = ""
            )
        }

        scope.launch {
            try {
                SchoolStaffRepository.deleteSchoolStaff(row.id).getOrThrow()
                refreshStaffAfterMutation()
                _uiState.update {
                    it.copy(
                        isLoading = true,
                        infoMessage = "Deleted ${row.staffName}"
                    )
                }
                scLog(
                    "$TAG deleteStaff success school=$normalizedSchoolId staffId=${row.id}"
                )
            } catch (throwable: Throwable) {
                scLog("$TAG deleteStaff failure school=$normalizedSchoolId staffId=${row.id}", throwable)
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        errorMessage = throwable.message.orEmpty().ifBlank { "Failed to delete staff." }
                    )
                }
            }
        }
    }

    private fun openEditStaff(staffId: String) {
        val row = _uiState.value.staff.firstOrNull { it.id == staffId }
        if (row == null) {
            _uiState.update { it.copy(errorMessage = "Staff not found", infoMessage = "") }
            return
        }
        _uiState.update { state ->
            state.copy(
                isEditStaffModalOpen = true,
                isAddStaffModalOpen = false,
                editingStaffId = row.id,
                editingStaffFormValues = staffFormValuesFrom(row, state.authRoleOptions),
                isEditingStaffLoading = true,
                errorMessage = "",
                infoMessage = ""
            )
        }
        loadAssignmentOptions(forceRemote = true)
        scope.launch {
            SchoolStaffRepository.loadSchoolStaffWithAssignments(row.id).fold(
                onSuccess = { response ->
                    _uiState.update { state ->
                        if (state.editingStaffId != row.id) state else state.copy(
                            editingStaffFormValues = staffFormValuesFrom(response, state.authRoleOptions),
                            isEditingStaffLoading = false,
                            errorMessage = ""
                        )
                    }
                },
                onFailure = { throwable ->
                    _uiState.update { state ->
                        if (state.editingStaffId != row.id) state else state.copy(
                            isEditingStaffLoading = false,
                            errorMessage = throwable.message.orEmpty().ifBlank { "Unable to load staff assignments." }
                        )
                    }
                }
            )
        }
    }

    private fun saveStaff(
        staffId: String?,
        values: StaffFormValues
    ) {
        val validationError = validateStaffForm(values)
        if (validationError.isNotBlank()) {
            _uiState.update {
                it.copy(
                    errorMessage = validationError,
                    infoMessage = ""
                )
            }
            return
        }

        val currentUserId = UserSessionStore.getUserId().trim().takeIf { it.isNotBlank() }
        val successPrefix = if (staffId.isNullOrBlank()) "Created" else "Updated"
        val apiAuthRole = staffAuthRoleCode(values.authRole, _uiState.value.authRoleOptions)

        _uiState.update {
            it.copy(
                isLoading = true,
                isSaving = true,
                errorMessage = "",
                infoMessage = ""
            )
        }

        scope.launch {
            try {
                if (staffId.isNullOrBlank()) {
                    val teachingAssignments = values.teachingAssignments
                        .filter { it.classSectionId.isNotBlank() }
                        .map {
                            TeachingAssignmentInput(
                                classSectionId = it.classSectionId,
                                subjectIds = it.subjectIds,
                                isClassTeacher = it.isClassTeacher
                            )
                        }
                        .takeIf { it.isNotEmpty() }
                    if (teachingAssignments == null) {
                        SchoolStaffRepository.createSchoolStaff(
                            SchoolStaffCreateRequest(
                                schoolId = normalizedSchoolId,
                                employeeId = values.staffId.trim(),
                                name = values.staffName.trim(),
                                roleTitle = values.staffRole.trim().ifBlank { null },
                                authRole = apiAuthRole,
                                gender = com.schoolconnect.app.repository.systemlookup.SystemLookupOptions.serverCode("GENDER", values.gender),
                                customAttributes = emptyMap(),
                                isActive = true,
                                mobile = values.mobileNumber.trim(),
                                email = values.emailAddress.trim().ifBlank { null }
                            )
                        ).getOrThrow()
                    } else {
                        SchoolStaffRepository.createSchoolStaffWithAssignments(
                            request = AddStaffWithAssignmentsRequest(
                            schoolId = normalizedSchoolId,
                            employeeId = values.staffId.trim(),
                            name = values.staffName.trim(),
                            roleTitle = values.staffRole.trim().ifBlank { null },
                            authRole = apiAuthRole,
                            gender = com.schoolconnect.app.repository.systemlookup.SystemLookupOptions.serverCode("GENDER", values.gender),
                            customAttributes = emptyMap(),
                            isActive = true,
                            mobile = values.mobileNumber.trim(),
                            email = values.emailAddress.trim().ifBlank { null },
                                teachingAssignments = teachingAssignments
                            ),
                            currentUserId = currentUserId
                        ).getOrThrow()
                    }
                } else {
                    SchoolStaffRepository.updateSchoolStaffWithAssignments(
                        staffId = staffId,
                        request = UpdateStaffWithAssignmentsRequest(
                            employeeId = values.staffId.trim(),
                            name = values.staffName.trim(),
                            roleTitle = values.staffRole.trim().ifBlank { null },
                            authRole = apiAuthRole,
                            gender = com.schoolconnect.app.repository.systemlookup.SystemLookupOptions.serverCode("GENDER", values.gender),
                            isActive = staffStatusIsActive(values.status),
                            mobile = values.mobileNumber.trim(),
                            email = values.emailAddress.trim().ifBlank { null },
                            teachingAssignments = values.teachingAssignments
                                .filter { it.classSectionId.isNotBlank() }
                                .map {
                                    TeachingAssignmentInput(
                                        classSectionId = it.classSectionId,
                                        subjectIds = it.subjectIds,
                                        isClassTeacher = it.isClassTeacher
                                    )
                                }
                                .takeIf { it.isNotEmpty() }
                        ),
                        currentUserId = currentUserId
                    ).getOrThrow()
                }

                SchoolStaffRepository.invalidateCaches()
                AppToastCenter.showSuccess(
                    if (staffId.isNullOrBlank()) "Staff added successfully" else "Staff updated successfully"
                )
                refreshStaffAfterMutation()
                _uiState.update {
                    it.copy(
                        isAddStaffModalOpen = false,
                        isEditStaffModalOpen = false,
                        editingStaffId = "",
                        isSaving = false,
                        isLoading = true,
                        errorMessage = it.errorMessage,
                        infoMessage = ""
                    )
                }
                scLog(
                    "$TAG saveStaff success school=$normalizedSchoolId staffId=${staffId.orEmpty().ifBlank { values.staffId.trim() }} mode=$successPrefix"
                )
            } catch (throwable: Throwable) {
                scLog("$TAG saveStaff failure school=$normalizedSchoolId staffId=${staffId.orEmpty().ifBlank { values.staffId.trim() }}", throwable)
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        isSaving = false,
                        errorMessage = throwable.message.orEmpty().ifBlank { "Failed to save staff" }
                    )
                }
            }
        }
    }

    private fun validateStaffForm(values: StaffFormValues): String {
        if (values.staffId.isBlank()) return "Staff ID is required."
        if (values.staffName.isBlank()) return "Staff name is required."
        if (values.staffRole.isBlank()) return "Staff role is required."
        if (values.authRole.isBlank()) return "Auth role is required."
        if (values.mobileNumber.isBlank()) return "Mobile number is required."
        if (!com.schoolconnect.app.util.isValidMobileNumber(values.mobileNumber.trim())) return "Enter a valid mobile number."
        if (values.gender.isBlank()) return "Gender is required."
        if (values.emailAddress.isBlank()) return "Email address is required."
        if (!com.schoolconnect.app.util.isValidEmail(values.emailAddress)) return "Enter a valid email address."
        if (values.status.isBlank()) return "Status is required."
        return ""
    }

    private fun openViewStaff(staffId: String) {
        val row = _uiState.value.staff.firstOrNull { it.id == staffId }
        if (row == null) {
            _uiState.update { it.copy(isViewStaffModalOpen = false, viewingStaffId = "", errorMessage = "Staff not found", infoMessage = "") }
            return
        }
        _uiState.update { it.copy(isViewStaffModalOpen = true, viewingStaffId = row.id, errorMessage = "", infoMessage = "") }
        scope.launch {
            SchoolStaffRepository.loadSchoolStaffWithAssignments(row.id).fold(
                onSuccess = { response ->
                    _uiState.update { state ->
                        if (!state.isViewStaffModalOpen || state.viewingStaffId != row.id) state
                        else state.copy(
                            staff = state.staff.map { current -> if (current.id == row.id) current.withViewDetails(response) else current }
                        )
                    }
                },
                onFailure = { throwable ->
                    scLog("$TAG View staff detail failed for staffId=${row.id}: ${throwable.message}", throwable)
                }
            )
        }
    }

    private fun StaffRowUi.withViewDetails(response: SchoolStaffWithAssignmentsResponse): StaffRowUi {
        val baseResponse = response.toSchoolStaffResponse()
        val roleLabel = response.roleTitle.orEmpty().ifBlank { response.authRole.orEmpty() }
        val authRoleCode = canonicalStaffAuthRoleCode(response.authRole.orEmpty()).ifBlank { authRoleCode }
        return copy(
            staffId = response.employeeId.orEmpty().ifBlank { staffId },
            staffName = response.name.orEmpty().ifBlank { staffName },
            roleTitle = roleLabel.ifBlank { roleTitle },
            staffRole = parseStaffRole(roleLabel.ifBlank { staffRoleLabel(staffRole) }),
            authRole = parseStaffAuthRole(authRoleCode),
            authRoleCode = authRoleCode,
            mobileNumber = baseResponse.resolvedMobile.ifBlank { mobileNumber },
            gender = if (response.gender.isNullOrBlank()) gender else parseStaffGender(response.gender.orEmpty()),
            emailAddress = response.email.orEmpty().ifBlank { emailAddress },
            status = if (response.isActive == true) StaffStatusUi.ACTIVE else if (response.isActive == false) StaffStatusUi.INACTIVE else status,
            createdAt = response.createdAt.orEmpty().ifBlank { createdAt },
            updatedAt = response.updatedAt.orEmpty().ifBlank { updatedAt },
            viewAssignments = staffViewAssignmentsFrom(response)
        )
    }

    private fun toStaffRowUi(response: SchoolStaffResponse): StaffRowUi {
        val roleLabel = response.roleTitle.orEmpty().ifBlank { response.authRole.orEmpty() }
        val authRoleCode = canonicalStaffAuthRoleCode(response.authRole.orEmpty())
        return StaffRowUi(
            id = response.id.orEmpty().ifBlank { response.employeeId.orEmpty().ifBlank { response.userId.orEmpty() } },
            staffId = response.employeeId.orEmpty().ifBlank { response.id.orEmpty().take(8) },
            staffName = response.name.orEmpty().trim(),
            staffRole = parseStaffRole(roleLabel),
            roleTitle = roleLabel.trim(),
            authRole = parseStaffAuthRole(authRoleCode),
            authRoleCode = authRoleCode,
            mobileNumber = response.resolvedMobile.ifBlank { "—" },
            gender = parseStaffGender(response.gender.orEmpty()),
            emailAddress = response.email.orEmpty().trim(),
            status = if (response.isActive == true) StaffStatusUi.ACTIVE else StaffStatusUi.INACTIVE,
            createdAt = response.createdAt.orEmpty(),
            updatedAt = response.updatedAt.orEmpty()
        )
    }

    private fun parseStaffRole(value: String): StaffRoleUi = when (value.trim().lowercase()) {
        "teacher" -> StaffRoleUi.TEACHER
        "principal" -> StaffRoleUi.PRINCIPAL
        "coordinator" -> StaffRoleUi.COORDINATOR
        "librarian" -> StaffRoleUi.LIBRARIAN
        "accountant" -> StaffRoleUi.ACCOUNTANT
        "vice principal", "vice-principal", "viceprincipal" -> StaffRoleUi.VICE_PRINCIPAL
        "counselor" -> StaffRoleUi.COUNSELOR
        "security" -> StaffRoleUi.SECURITY
        "lab assistant", "lab-assistant", "labassistant" -> StaffRoleUi.LAB_ASSISTANT
        else -> StaffRoleUi.OTHER
    }

    private fun parseStaffAuthRole(value: String): StaffAuthRoleUi = when (value.trim().uppercase()) {
        "SCHOOL_ADMIN" -> StaffAuthRoleUi.ADMIN
        "SCHOOL_MGMT" -> StaffAuthRoleUi.MANAGEMENT
        "SCHOOL_TEACHER", "SCHOOL_SUPPORT_STAFF" -> StaffAuthRoleUi.STAFF
        else -> StaffAuthRoleUi.STAFF
    }

    private fun parseStaffGender(value: String): StaffGenderUi {
        val normalized = value.trim()
        val label = com.schoolconnect.app.repository.systemlookup.SystemLookupOptions
            .label("GENDER", normalized)
            .trim()
            .lowercase()
        return when {
            label == "female" || normalized.lowercase() in setOf("f", SchoolKeys.V_GENDER_F.lowercase()) -> StaffGenderUi.FEMALE
            label == "other" || normalized.lowercase() in setOf("o", "other") -> StaffGenderUi.OTHER
            else -> StaffGenderUi.MALE
        }
    }

    private fun String.toActiveFilter(): Boolean? = when (trim().lowercase()) {
        "active" -> true
        "inactive" -> false
        else -> null
    }
}
